// The single animation loop in the game.
//
// Rendering used to be event-driven: frames were only painted while a banana
// was in the air or a slider moved, which meant nothing on screen could idle.
// The loop now runs for the whole session, so the gorillas can breathe and
// impact debris actually settles instead of freezing mid-air.

import { state } from '../state.js';
import { isAttract } from '../state.js';
import { advanceAntics } from '../antics.js';
import { advanceProjectile, advanceDrone, advanceLeap, advanceSheep, advanceTurnClock, announceLoot } from '../flow.js';
import { advanceBubbles } from '../banter.js';
import { advanceSplashScene } from '../splash.js';
import { advanceCrate } from '../crates.js';
import { advanceQuake, advanceWindows } from '../city.js';
import { advanceWeather } from '../weather.js';
import { matchOnHold } from '../screens.js';
import { watchTurnCall } from './announce.js';
import { draw } from './index.js';

let running = false;

export function startLoop() {
  if (running) return;
  running = true;
  state.raf = requestAnimationFrame(frame);
}

function frame(now) {
  // The next frame is asked for FIRST, before any of the work below, and that is the whole
  // difference between losing a frame and losing the game.
  //
  // This used to be the last line. An exception anywhere in here — a null element, a bad
  // number, anything at all — meant the line that schedules the next frame never ran, so the
  // loop simply stopped: mid-frame, with the canvas frozen on the last thing it had managed
  // to paint and no way back but a reload. Not hypothetical — measured, from a crate whose
  // reward had nowhere to be shown: state.clock stopped dead at 8512 and never moved again,
  // and the canvas did not change a pixel in 400ms. Nothing in the game cancels the handle
  // (there is no cancelAnimationFrame in the project), so asking first costs nothing.
  //
  // Asking first makes a bad frame cost a frame: the throw still reaches the console, which
  // is where it belongs, and the next one carries on.
  state.raf = requestAnimationFrame(frame);

  // `now` is a high-resolution timestamp, which is exactly what the idle
  // animation in gorilla.js wants to phase against.
  state.clock = now;

  advanceAntics();
  advanceWeather(now);
  advanceBubbles();
  advanceDrone();
  // The turn clock, which only ever ends a turn. It sits with the other per-frame
  // steps rather than in the shot logic because a turn can run out with nobody
  // having touched anything.
  advanceTurnClock();
  // And the nudge to whoever's turn has just come round. Next to the clock because it
  // is the same kind of step: something derived from the turn itself rather than an
  // event that five different places have to remember to fire. After the clock, so a
  // turn lost to time is announced in the same frame it is lost.
  watchTurnCall();
  // The opening scene runs on the front end, and the front end is drawn by this
  // same loop. That is the whole reason it can exist at all.
  advanceSplashScene();
  // The crate drops whether or not anybody is shooting, and it drops on the match's own time — but
  // a paused match is not a match: a reward that lands behind the overlay is a reward nobody saw
  // land, and its toast is an announcement of something that has not happened yet. It is the one
  // step in here that reads the board rather than the clock, so it is held with the shots.
  //
  // `matchOnHold` is the other half of the same idea — a phone held upright (see screens.js) — and
  // it holds the same three steps. The backdrop keeps running under the notice, as it does under
  // the pause.
  //
  // And not only the pause: out of the game screen the match does not run either. The CPU thinks
  // and fires from wherever the player happens to be standing — `maybeCpuTurn` never asks about
  // the screen — so a match left standing in the menu could resolve itself and jump to the results
  // screen on top of the menu. The turn clock has always read it this way (`advanceTurnClock` asks
  // for `game`); this is the same question asked here.
  const matchHeld = state.screen !== 'game' || matchOnHold();

  // El regalo, fuera de la partida, sólo hay que retirarlo: `advanceCrate` lo deja en null cuando no
  // hay ronda, y hasta ahora no se le preguntaba fuera de la partida, así que una caja de la última
  // se quedaba donde estuviera —a media caída, o colgada en el aire— y ahí se quedaba para siempre.
  // Sin terremoto no se notaba: estaba quieta y su fondo también. Con la ciudad temblando (ver
  // isAttract) se veía flotando en su sitio mientras los edificios subían y bajaban.
  if (isAttract()) {
    advanceCrate();
  } else if (!matchHeld) {
    const loot = advanceCrate();
    if (loot) announceLoot(loot);
  }

  // The neighbours, on their own clock: one building at a time, a window every so
  // often, and nothing anywhere reads it back. It runs on the front end too, where
  // the same city is standing under the same sky.
  advanceWindows();

  // And the city itself, in the mode that shakes: the skyline is moved before anything is
  // drawn, so the banana's collision, the fists and the perches all agree about where the
  // ground is this frame.
  advanceQuake();

  // The sheep, after the quake: it lands on this frame's rooftops, not on last frame's, which
  // is what keeps it standing on the city the same way the gorillas do.
  advanceSheep();

  // A paused match does not resolve shots behind the overlay, but the sky and
  // the gorillas carry on: they are the attract mode the menus sit on top of.
  if (!matchHeld) {
    if (state.projectile) advanceProjectile();
    // A leap is stepped by the same loop and for the same reason: the banana is
    // released on one exact frame of the jump.
    if (state.leap) advanceLeap();
  }

  draw();
}
